import * as A from 'astronomy-engine';
import { Vec3, norm } from '../lib/math';

// Two-body orbits for comets (MPC CometEls.txt) and bright asteroids (JPL SBDB elements).
// Accurate to about an arcminute over a few months from the element epoch, which is plenty
// for showing where to look.

const K = 0.01720209895; // Gaussian gravitational constant, rad/day
const EPS = (23.4392911 * Math.PI) / 180;
const C_AU_PER_DAY = 173.1446327;
const DEG = Math.PI / 180;
const JD_UNIX = 2440587.5;

export interface CometElements {
  kind: 'comet';
  name: string;
  tp: number; // perihelion time, JD (TT)
  q: number;
  e: number;
  i: number;
  node: number;
  peri: number;
  H: number;
  G: number; // MPC comet slope: m = H + 5 log Δ + 2.5 G log r
}

export interface AsteroidElements {
  kind: 'asteroid';
  id: number | null;
  name: string;
  epochJD: number;
  a: number;
  e: number;
  i: number;
  node: number;
  peri: number;
  M: number;
  H: number;
  G: number;
  diameterKm: number | null;
}

export type SmallBody = CometElements | AsteroidElements;

export const jdOf = (d: Date) => d.getTime() / 86400000 + JD_UNIX;

/** Parse the MPC "CometEls.txt" one-line format. */
export function parseCometEls(text: string): CometElements[] {
  const out: CometElements[] = [];
  for (const line of text.split('\n')) {
    if (line.length < 100) continue;
    const y = +line.slice(14, 18), m = +line.slice(19, 21), d = +line.slice(22, 29);
    const q = +line.slice(30, 39), e = +line.slice(41, 49);
    const peri = +line.slice(51, 59), node = +line.slice(61, 69), inc = +line.slice(71, 79);
    const H = parseFloat(line.slice(91, 95)), G = parseFloat(line.slice(96, 100));
    const name = line.slice(102, 158).trim();
    if (![y, m, d, q, e].every(Number.isFinite) || !name) continue;
    const day = Math.floor(d);
    const tp = jdOf(new Date(Date.UTC(y, m - 1, day))) + (d - day);
    out.push({ kind: 'comet', name, tp, q, e, i: inc, node, peri, H: Number.isFinite(H) ? H : 15, G: Number.isFinite(G) ? G : 4 });
  }
  return out;
}

/** Heliocentric position in the orbital plane (x towards perihelion), AU. */
function planePosition(b: SmallBody, jd: number): [number, number] {
  if (b.kind === 'asteroid') {
    const n = K / Math.pow(b.a, 1.5);
    const M = (b.M * DEG + n * (jd - b.epochJD)) % (2 * Math.PI);
    const E = solveElliptic(M, b.e);
    return [b.a * (Math.cos(E) - b.e), b.a * Math.sqrt(1 - b.e * b.e) * Math.sin(E)];
  }
  const dt = jd - b.tp;
  if (b.e < 0.999) {
    const a = b.q / (1 - b.e);
    const M = (K / Math.pow(a, 1.5)) * dt;
    const E = solveElliptic(M, b.e);
    return [a * (Math.cos(E) - b.e), a * Math.sqrt(1 - b.e * b.e) * Math.sin(E)];
  }
  if (b.e > 1.001) {
    const a = b.q / (b.e - 1);
    const M = (K / Math.pow(a, 1.5)) * dt;
    let H = Math.asinh(M / b.e);
    for (let k = 0; k < 50; k++) {
      const f = b.e * Math.sinh(H) - H - M;
      const dH = f / (b.e * Math.cosh(H) - 1);
      H -= dH;
      if (Math.abs(dH) < 1e-12) break;
    }
    return [a * (b.e - Math.cosh(H)), a * Math.sqrt(b.e * b.e - 1) * Math.sinh(H)];
  }
  // Near-parabolic: Barker's equation for the true anomaly, then r from q and e.
  const W = (3 * K * dt) / Math.sqrt(2 * b.q * b.q * b.q);
  const Y = Math.cbrt(W / 2 + Math.sqrt((W * W) / 4 + 1));
  const s = Y - 1 / Y; // tan(ν/2)
  const nu = 2 * Math.atan(s);
  const r = (b.q * (1 + b.e)) / (1 + b.e * Math.cos(nu));
  return [r * Math.cos(nu), r * Math.sin(nu)];
}

function solveElliptic(M: number, e: number): number {
  let E = e < 0.8 ? M : Math.PI;
  for (let k = 0; k < 60; k++) {
    const dE = (E - e * Math.sin(E) - M) / (1 - e * Math.cos(E));
    E -= dE;
    if (Math.abs(dE) < 1e-13) break;
  }
  return E;
}

/** Heliocentric J2000 equatorial position, AU. */
export function helioEquatorial(b: SmallBody, jd: number): Vec3 {
  const [x, y] = planePosition(b, jd);
  const w = b.peri * DEG, O = b.node * DEG, i = b.i * DEG;
  const cw = Math.cos(w), sw = Math.sin(w), cO = Math.cos(O), sO = Math.sin(O), ci = Math.cos(i), si = Math.sin(i);
  // Orbital plane → ecliptic J2000.
  const xe = (cO * cw - sO * sw * ci) * x + (-cO * sw - sO * cw * ci) * y;
  const ye = (sO * cw + cO * sw * ci) * x + (-sO * sw + cO * cw * ci) * y;
  const ze = sw * si * x + cw * si * y;
  // Ecliptic → equatorial.
  return [xe, ye * Math.cos(EPS) - ze * Math.sin(EPS), ye * Math.sin(EPS) + ze * Math.cos(EPS)];
}

export interface SmallBodyState {
  dir: Vec3; // geocentric J2000 unit vector (light-time corrected)
  r: number; // from the Sun, AU
  delta: number; // from Earth, AU
  mag: number;
  phase: number; // degrees
  antiSun: Vec3; // unit vector pointing away from the Sun at the body (tail direction), J2000
}

export function smallBodyState(b: SmallBody, date: Date): SmallBodyState {
  const jd = jdOf(date);
  const ev = A.HelioVector(A.Body.Earth, date);
  const earth: Vec3 = [ev.x, ev.y, ev.z];
  let p = helioEquatorial(b, jd);
  let g: Vec3 = [p[0] - earth[0], p[1] - earth[1], p[2] - earth[2]];
  // One light-time iteration is plenty.
  const tau = Math.hypot(...g) / C_AU_PER_DAY;
  p = helioEquatorial(b, jd - tau);
  g = [p[0] - earth[0], p[1] - earth[1], p[2] - earth[2]];
  const r = Math.hypot(...p), delta = Math.hypot(...g);
  const cosPhase = (p[0] * g[0] + p[1] * g[1] + p[2] * g[2]) / (r * delta);
  const alpha = Math.acos(Math.max(-1, Math.min(1, cosPhase)));
  let mag: number;
  if (b.kind === 'comet') {
    mag = b.H + 5 * Math.log10(delta) + 2.5 * b.G * Math.log10(r);
  } else {
    const t = Math.tan(alpha / 2);
    const phi1 = Math.exp(-3.33 * Math.pow(t, 0.63));
    const phi2 = Math.exp(-1.87 * Math.pow(t, 1.22));
    mag = b.H + 5 * Math.log10(r * delta) - 2.5 * Math.log10((1 - b.G) * phi1 + b.G * phi2);
  }
  return { dir: norm(g), r, delta, mag, phase: alpha / DEG, antiSun: norm(p) };
}
